import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ref, update } from "firebase/database";
import {
  LuSettings,
  LuBadgeCheck,
  LuStar,
  LuChevronRight,
  LuUserPlus,
  LuMessageCircle,
} from "react-icons/lu";
import { database } from "../constants/firebase";
import { useUser } from "../contexts/UserContext";
import {
  dateValue,
  money,
  paid,
  rows,
  safeUrl,
  useRecord,
  userVisible,
} from "../utils/appData";
import {
  displayName,
  profileNiches,
  friendStatus,
  friendshipPatch,
} from "../utils/discoverData";
import AppPage, { State } from "./AppPage";
import Avatar from "./UserAvatar";
import ProfileExtras from "./ProfileExtras";
import ModalSheet from "./ModalSheet";
export default function ProfileView({ uid }) {
  const { currentUser } = useUser();
  const owner = uid === currentUser?.uid;
  const state = useRecord(uid ? `Users/${uid}` : null);
  const profile = state.data;
  const jobState = useRecord(uid ? "Jobs" : null);
  const reviewState = useRecord(uid ? `Reviews/${uid}` : null);
  const sales = useRecord(owner && uid ? `Users/${uid}/jobSalesHistory` : null);
  const applications = useRecord(owner ? "JobApplications" : null);
  const meState = useRecord(
    !owner && currentUser?.uid ? `Users/${currentUser.uid}` : null,
  );
  const [friendsOpen, setFriendsOpen] = useState(false);
  const [friendSearch, setFriendSearch] = useState("");
  const directory = useRecord(friendsOpen ? "Users" : null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    setFriendsOpen(false);
    setFriendSearch("");
    setError("");
  }, [uid]);
  const jobs = rows(jobState.data);
  const booked = (j) =>
    Boolean(
      typeof j.hiredApplicantId === "string" && j.hiredApplicantId.trim(),
    );
  const ids = new Set(
    jobs
      .filter(
        (j) =>
          (j.hostId === uid && (booked(j) || j.completedAt)) ||
          (j.hiredApplicantId === uid && booked(j)),
      )
      .map((j) => j.id),
  );
  if (owner)
    rows(sales.data).forEach((sale) =>
      ids.add(sale.jobId || `sale:${sale.id}`),
    );
  const crew = jobs.filter((j) => j.hostId === uid && booked(j)).length;
  const reviews = rows(reviewState.data).filter(
    (r) =>
      Number.isInteger(Number(r.rating)) &&
      Number(r.rating) >= 1 &&
      Number(r.rating) <= 5,
  );
  const average = reviews.length
    ? reviews.reduce((s, r) => s + Number(r.rating), 0) / reviews.length
    : 0;
  const now = new Date();
  const monthly = rows(sales.data).filter(
    (r) =>
      paid(r) &&
      new Date(dateValue(r.createdAt)).getMonth() === now.getMonth() &&
      new Date(dateValue(r.createdAt)).getFullYear() === now.getFullYear(),
  );
  const earned = {};
  monthly.forEach((r) => {
    const c = String(r.currency || "ZAR").toUpperCase();
    earned[c] =
      (earned[c] || 0) +
      Number(r.amount ?? r.settledAmount ?? r.saleAmount ?? 0);
  });
  const earnedText =
    Object.entries(earned)
      .map(([c, n]) => money(n, c))
      .join(" · ") || money(0);
  const ownApps = Object.values(applications.data || {})
    .map((a) => a?.[uid])
    .filter(Boolean);
  const active = ownApps.filter((a) =>
    ["applied", "selected", "shortlisted"].includes(a.status),
  ).length;
  const hired = ownApps.filter((a) => a.status === "hired").length;
  const friends = Object.keys(profile?.friends || {}).filter(
    (id) => profile.friends[id],
  );
  const pending = owner
    ? Object.values(profile?.friendRequests || {}).filter(
      (v) => v === "received",
    ).length
    : 0;
  const header = useMemo(
    () =>
      owner ? (
        <Link
          to="/settings"
          aria-label="Settings"
          className="grid h-10 w-10 place-items-center rounded-full border border-black/5 bg-white dark:border-[#363e46] dark:bg-[#23272c]"
        >
          <LuSettings size={22} />
        </Link>
      ) : undefined,
    [owner],
  );
  let website = safeUrl(profile?.website || profile?.websiteUrl || "");
  if (!website && profile?.website)
    website = safeUrl(`https://${profile.website}`);
  const names = profileNiches(profile);
  const friendPeople = friends
    .map((id) => (directory.data?.[id] ? { ...directory.data[id], id } : null))
    .filter((p) => p && userVisible(p))
    .filter((p) =>
      displayName(p).toLowerCase().includes(friendSearch.toLowerCase()),
    );
  async function friend() {
    if (busy || meState.loading || !currentUser?.uid) return;
    setBusy(true);
    try {
      await update(
        ref(database),
        friendshipPatch(
          currentUser.uid,
          uid,
          friendStatus(
            meState.data || currentUser,
            { ...profile, id: uid },
            currentUser.uid,
          ),
        ),
      );
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  const status = friendStatus(
    meState.data,
    { ...profile, id: uid },
    currentUser?.uid,
  );
  return (
    <AppPage title="Profile" headerActions={header}>
      <State {...state} empty={!profile}>
        Profile unavailable.
      </State>
      {profile &&
        (!owner && !userVisible(profile) ? (
          <p className="text-sm text-slate-500">This profile is unavailable.</p>
        ) : (
          <>
            <section className="mb-3 rounded-[22px] border border-black/5 bg-white p-4 shadow-sm dark:border-[#363e46] dark:bg-[#23272c]">
              <div className="flex items-start gap-3">
                <Avatar
                  user={profile}
                  className="h-[72px] w-[72px] shrink-0 rounded-full object-cover max-[359px]:h-14 max-[359px]:w-14"
                />

                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <h2
                      className={`flex min-w-0 flex-1 items-start gap-1.5 break-words text-xl font-extrabold leading-6 ${profile.role === "Host"
                        ? "text-[#B8860B] dark:text-[#EFBF04]"
                        : ""
                        }`}
                    >
                      {displayName(profile)}

                      {profile.isTeamUser && (
                        <LuBadgeCheck
                          size={17}
                          className="mt-1 shrink-0 text-[#327691]"
                        />
                      )}
                    </h2>

                    {owner && (
                      <Link
                        to="/edit-profile"
                        className="shrink-0 rounded-full bg-[#edf3f5] px-3 py-1.5 text-xs font-bold text-[#327691] dark:bg-[#22333a] dark:text-[#5FAFCB]"
                      >
                        Edit
                      </Link>
                    )}
                  </div>

                  <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                    {names[0] || profile.niche || "Creator"}
                    {names.length > 1 ? ` +${names.length - 1}` : ""}
                    {profile.location || profile.country
                      ? ` · ${profile.location || profile.country}`
                      : ""}
                  </p>

                  {names.length > 1 && (
                    <p className="mt-1 text-[10px] text-slate-400">
                      Also: {names.slice(1).join(", ")}
                    </p>
                  )}

                  {website && (
                    <a
                      href={website}
                      rel="noreferrer"
                      target="_blank"
                      className="mt-1.5 block truncate text-xs font-semibold text-[#327691] dark:text-[#5FAFCB]"
                    >
                      {website.replace(/^https?:\/\//, "").replace(/\/$/, "")}
                    </a>
                  )}

                  {/* Stats stay together beneath the profile details */}
                  <div className="mt-2 flex flex-nowrap items-center gap-2 overflow-x-auto whitespace-nowrap pb-1 text-[11px] text-slate-500 [scrollbar-width:none] dark:text-slate-400 [&::-webkit-scrollbar]:hidden">
                    <span className="shrink-0">
                      <b className="text-slate-900 dark:text-white">
                        {jobState.error ? "—" : ids.size}
                      </b>{" "}
                      {ids.size === 1 ? "job" : "jobs"}
                    </span>

                    <span className="shrink-0">·</span>

                    <span className="shrink-0">
                      <b className="text-slate-900 dark:text-white">
                        {jobState.error ? "—" : crew}
                      </b>{" "}
                      crew hired
                    </span>

                    <span className="shrink-0">·</span>

                    <button
                      onClick={() =>
                        document
                          .getElementById("profile-reviews")
                          ?.scrollIntoView({ behavior: "smooth" })
                      }
                      className="flex shrink-0 items-center gap-1"
                    >
                      <LuStar size={12} fill="currentColor" className="shrink-0" />
                      {reviews.length
                        ? `${average.toFixed(1)} (${reviews.length})`
                        : "New"}
                    </button>

                    <span className="shrink-0">·</span>

                    <button
                      className="shrink-0"
                      onClick={() => {
                        setFriendSearch("");
                        setFriendsOpen(true);
                      }}
                    >
                      <b className="text-slate-900 dark:text-white">
                        {friends.length}
                      </b>{" "}
                      {friends.length === 1 ? "friend" : "friends"}
                    </button>
                  </div>
                </div>
              </div>

              {/* Bio uses the full card width */}
              {profile.about && (
                <p className="mt-3 whitespace-pre-wrap break-words text-[13px] leading-5">
                  {profile.about}
                </p>
              )}
            </section>
            {owner ? (
              <section className="mb-5">
                <p className="mb-2 ml-1 text-[10px] font-bold tracking-wider text-slate-400">
                  ONLY YOU CAN SEE THIS
                </p>
                <div className="overflow-hidden rounded-[18px] bg-white shadow-sm dark:bg-[#23272c]">
                  {[
                    [
                      "Earned this month",
                      sales.loading
                        ? "Loading…"
                        : sales.error
                          ? "Unavailable"
                          : earnedText,
                      "/funds",
                    ],
                    [
                      "My applications",
                      applications.loading
                        ? "Loading…"
                        : applications.error
                          ? "Unavailable"
                          : active || hired
                            ? [
                              active ? `${active} active` : "",
                              hired ? `${hired} hired` : "",
                            ]
                              .filter(Boolean)
                              .join(" · ")
                            : "None yet",
                      "/my-applications",
                    ],
                  ].map(([label, value, path], i) => (
                    <Link
                      key={path}
                      to={path}
                      className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-4 py-4 last:border-0 dark:border-white/5"
                    >
                      <strong className="text-sm">{label}</strong>
                      <span
                        className={`flex items-center gap-1.5 text-xs font-semibold ${i === 0 ? "text-green-700 dark:text-green-400" : "text-slate-500"}`}
                      >
                        {value}
                        <LuChevronRight size={16} />
                      </span>
                    </Link>
                  ))}
                </div>
              </section>
            ) : (
              <div className="mb-5 flex gap-3">
                <button
                  onClick={friend}
                  disabled={busy || meState.loading}
                  className="flex min-h-11 flex-1 items-center justify-center gap-2 rounded-[14px] border border-[#327691] text-sm font-bold text-[#327691] disabled:opacity-40 dark:text-[#5FAFCB]"
                >
                  <LuUserPlus size={16} />
                  {busy
                    ? "Saving…"
                    : {
                      none: "Add friend",
                      sent: "Requested",
                      received: "Accept request",
                      friends: "Friends",
                    }[status]}
                </button>
                <Link
                  to={`/chat/${uid}`}
                  className="flex min-h-11 flex-1 items-center justify-center gap-2 rounded-[14px] bg-[#327691] text-sm font-bold text-white"
                >
                  <LuMessageCircle size={16} />
                  Message
                </Link>
              </div>
            )}
            {error && (
              <p role="alert" className="mb-3 text-sm text-red-600">
                {error}
              </p>
            )}
            <ProfileExtras uid={uid} owner={owner} />
          </>
        ))}
      <ModalSheet
        open={friendsOpen}
        onClose={() => setFriendsOpen(false)}
        title={`Friends (${friends.length})`}
        footer={`${friends.length} ${friends.length === 1 ? "friend" : "friends"}`}
      >
        <input
          aria-label="Search profile friends"
          value={friendSearch}
          onChange={(e) => setFriendSearch(e.target.value)}
          placeholder="Search friends..."
          className="mb-3 w-full rounded-xl border border-slate-200 bg-transparent p-3 text-sm dark:border-slate-600"
        />
        <State loading={directory.loading} error={directory.error} />
        {friendPeople.map((person) => (
          <Link
            key={person.id}
            to={`/user-profile/${person.id}`}
            onClick={() => setFriendsOpen(false)}
            className="mb-2 flex items-center gap-4 rounded-2xl p-3 hover:bg-slate-50 dark:hover:bg-slate-800"
          >
            <Avatar
              user={person}
              className="h-[52px] w-[52px] shrink-0 rounded-full object-cover"
            />
            <span className="min-w-0">
              <strong className="block break-words text-sm">
                {displayName(person)}
              </strong>
              <small className="block truncate text-xs text-slate-500">
                {profileNiches(person)[0] || person.niche || "Creator"}
              </small>
            </span>
          </Link>
        ))}
        {!directory.loading && !friendPeople.length && (
          <p className="py-6 text-center text-sm text-slate-500">
            No matching friends.
          </p>
        )}
      </ModalSheet>
    </AppPage>
  );
}
